import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, LoadingState, ErrorState, Card, SectionTitle, StatusBadge, Field, FormError, SubmitButton, ConfirmDialog } from "@/components/common";
import { SuspensionPolicy } from "@/components/pro/SuspensionPolicy";
import { TeamAccessCard, InviteList, TestFlagCard, ManualBillingCard, DangerZoneCard, StripeNotice, ClearDemoCard } from "@/components/admin/GarageAdminTools";
import { TempMemberButton } from "@/components/TempMemberButton";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { SlugStatus } from "@/pages/admin/AdminGarages";
import { SlugChangeConfirm } from "@/components/pro/GarageForms";
import { t } from "@/i18n";

const STATUSES = ["trial", "active", "past_due", "suspended", "cancelled"];

function SubscriptionCard({ detail, plans, onSaved }) {
  const sub = detail.subscription;
  const trialDate = sub.trial_ends_at ? sub.trial_ends_at.slice(0, 10) : "";
  const base = () => ({ status: sub.status, plan_id: sub.plan_id || "", notes: sub.notes || "", trial_ends_at: trialDate,
    custom_price_monthly: sub.custom_price_monthly ?? "", custom_price_yearly: sub.custom_price_yearly ?? "" });
  const [f, setF] = useState(base);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setF(base()), [sub.status, sub.plan_id, sub.notes, trialDate, sub.custom_price_monthly, sub.custom_price_yearly]); // eslint-disable-line react-hooks/exhaustive-deps
  const liveStripe = sub.stripe_subscription_id && !["canceled", "incomplete_expired"].includes(sub.stripe_status);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const price = (v) => (v === "" || v == null ? null : Number(v));
      await api.patch(`/admin/garages/${detail.garage.id}/subscription`, { ...f, trial_ends_at: f.trial_ends_at && f.trial_ends_at !== trialDate ? f.trial_ends_at : null,
        custom_price_monthly: price(f.custom_price_monthly), custom_price_yearly: price(f.custom_price_yearly), set_custom_price: true });
      toast.success(t("admin.subscriptionSaved"));
      setConfirm(false);
      onSaved();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const blocking = ["suspended", "cancelled"].includes(f.status) && f.status !== sub.status;
  const submit = (e) => { e.preventDefault(); blocking || (f.status !== sub.status && ["suspended", "cancelled"].includes(sub.status)) ? setConfirm(true) : save(); };

  return (
    <Card testId="subscription-card">
      <SectionTitle hint={t("admin.subscriptionHint")}>{t("admin.subscription")}</SectionTitle>
      <form onSubmit={submit} className="space-y-4">
        <FormError message={error} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("admin.col.subscription")}>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="subscription-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`subscription-status-option-${s}`}>{t(`status.${s}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={t("admin.col.plan")}>
            <Select value={f.plan_id} onValueChange={(v) => setF({ ...f, plan_id: v })}>
              <SelectTrigger data-testid="subscription-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("p7.admin.customMonthly")} htmlFor="sub-cpm" hint={t("p7.admin.customHint")}><Input id="sub-cpm" type="number" min={0} step="0.01" value={f.custom_price_monthly} onChange={(e) => setF({ ...f, custom_price_monthly: e.target.value })} data-testid="subscription-custom-monthly-input" /></Field>
          <Field label={t("p7.admin.customYearly")} htmlFor="sub-cpy"><Input id="sub-cpy" type="number" min={0} step="0.01" value={f.custom_price_yearly} onChange={(e) => setF({ ...f, custom_price_yearly: e.target.value })} data-testid="subscription-custom-yearly-input" /></Field>
        </div>
        <Field label={t("p7.admin.trialEndDate")} htmlFor="sub-trial"><Input id="sub-trial" type="date" value={f.trial_ends_at} onChange={(e) => setF({ ...f, trial_ends_at: e.target.value })} data-testid="subscription-trial-end-input" /></Field>
        <Field label={t("admin.notes")} htmlFor="sub-notes"><Textarea id="sub-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="subscription-notes-input" /></Field>
        {sub.trial_ends_at && <p className="text-xs text-slate-500">{t("admin.trialEnds", { date: fmtDate(sub.trial_ends_at) })}</p>}
        {liveStripe && <StripeNotice detail={detail} onDone={onSaved} />}
        <SubmitButton busy={busy} data-testid="subscription-save-button">{t("common.save")}</SubmitButton>
      </form>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        busy={busy}
        destructive={blocking}
        title={blocking ? t("admin.confirmSuspendTitle", { status: t(`status.${f.status}`) }) : t("admin.confirmReactivateTitle")}
        description={blocking ? t("admin.confirmSuspendText") : t("admin.confirmReactivateText")}
        confirmLabel={t("common.confirm")}
        onConfirm={save}
        testId="subscription-confirm-dialog"
      />
    </Card>
  );
}

function SlugCard({ detail, onChanged }) {
  const g = detail.garage;
  const [slug, setSlug] = useState(g.slug);
  const [confirm, setConfirm] = useState(false);
  const save = () => api.put(`/admin/garages/${g.id}/slug`, { slug }).then(() => { setConfirm(false); toast.success(t("common.saved")); onChanged(); })
    .catch((e) => { setConfirm(false); toast.error(errMsg(e)); });
  return (
    <Card testId="admin-slug-card">
      <SectionTitle hint={t("b11.slug.adminHint")}>{t("b11.slug.title")}</SectionTitle>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-[220px] flex-1">
          <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} data-testid="admin-slug-input" />
          {slug !== g.slug && <SlugStatus slug={slug} path="/admin/slug-available" />}
        </div>
        <Button disabled={!slug || slug === g.slug} onClick={() => setConfirm(true)} data-testid="admin-slug-save">{t("common.save")}</Button>
      </div>
      {g.slug_aliases?.length > 0 && <p className="mt-2 text-xs text-slate-500" data-testid="admin-slug-aliases">{t("b11.slug.aliases")}: {g.slug_aliases.join(", ")}</p>}
      <SlugChangeConfirm open={confirm} from={g.slug} to={slug} onCancel={() => setConfirm(false)} onConfirm={save} />
    </Card>
  );
}

function FeatureOverridesCard({ detail, onChanged }) {
  const ov = detail.subscription?.feature_overrides || {};
  const planF = detail.plan?.features || {};
  const setOv = (feature, v) => api.put(`/admin/garages/${detail.garage.id}/feature-overrides`, { feature, enabled: v === "inherit" ? null : v === "on" })
    .then(() => { toast.success(t("common.saved")); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Card testId="admin-feature-overrides-card">
      <SectionTitle hint={t("b10.overrides.hint")}>{t("b10.overrides.title")}</SectionTitle>
      <div className="divide-y divide-slate-100 text-sm">
        {Object.keys(detail.features || {}).map((k) => (
          <div key={k} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`feature-override-row-${k}`}>
            <span className="font-medium">{t(`plan.features.${k}`)}</span>
            <span className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{t("b10.overrides.plan")}: {planF[k] ? "✓" : "—"}</span>
              <Select value={k in ov ? (ov[k] ? "on" : "off") : "inherit"} onValueChange={(v) => setOv(k, v)}>
                <SelectTrigger className="h-8 w-32" data-testid={`feature-override-${k}`}><SelectValue /></SelectTrigger>
                <SelectContent>{["inherit", "on", "off"].map((o) => <SelectItem key={o} value={o}>{t(`b10.overrides.${o}`)}</SelectItem>)}</SelectContent>
              </Select>
              <StatusBadge status={detail.features[k] ? "active" : "inactive"} testId={`feature-effective-${k}`} />
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function InviteManagerCard({ detail, onSent }) {
  const { me } = useAuth();
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post(`/admin/garages/${detail.garage.id}/invite-manager`, f);
      toast.success(t(me.demo_mode ? "invite.createdDemo" : "invite.createdNoProvider"));
      setF({ name: "", email: "" });
      onSent();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card testId="invite-manager-card">
      <SectionTitle hint={t("admin.inviteManagerHint", { used: detail.seats_used, max: detail.plan?.max_internal_users ?? "—" })}>{t("admin.inviteManager")}</SectionTitle>
      <form onSubmit={submit} className="space-y-4">
        <FormError message={error} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("invite.fullName")} htmlFor="im-name"><Input id="im-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-manager-name-input" /></Field>
          <Field label={t("auth.email")} htmlFor="im-email"><Input id="im-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-manager-email-input" /></Field>
        </div>
        <SubmitButton busy={busy} data-testid="invite-manager-submit-button">{t("admin.createInvite")}</SubmitButton>
      </form>
      <InviteList detail={detail} onChanged={onSent} />
    </Card>
  );
}

export default function AdminGarageDetail() {
  const { id } = useParams();
  const { data, error, loading, reload } = useFetch(`/admin/garages/${id}`);
  const plans = useFetch("/admin/plans");
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const g = data.garage;
  return (
    <div>
      <Link to="/admin/garages" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-garages-link"><ArrowLeft className="h-4 w-4" /> {t("admin.nav.garages")}</Link>
      <PageHeader
        eyebrow={g.is_demo ? t("common.demo") : g.is_test ? t("admin.testGarage") : t("admin.garage")}
        title={g.name}
        subtitle={`/g/${g.slug} · ${g.timezone} · ${g.currency}`}
        actions={<Button asChild variant="outline" data-testid="admin-open-portal-link"><a href={`/g/${g.slug}`} target="_blank" rel="noreferrer">{t("admin.openPortal")} <ExternalLink className="ml-2 h-4 w-4" /></a></Button>}
      />
      <div className="mb-6 flex flex-wrap gap-2">
        <StatusBadge status={data.subscription.status} testId="garage-detail-status" />
        <StatusBadge status={g.public_link_active ? "live" : "offline"} />
        <StatusBadge status={g.onboarding?.complete ? "complete" : "incomplete"} />
        <span className="text-xs text-slate-500" data-testid="garage-signup-source">{t(`p7.admin.source.${g.signup_source || "admin"}`)}</span>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <SubscriptionCard detail={data} plans={plans.data || []} onSaved={reload} />
        <SlugCard key={data.garage.slug} detail={data} onChanged={reload} />
        <FeatureOverridesCard detail={data} onChanged={reload} />
        <InviteManagerCard detail={data} onSent={reload} />
        <TeamAccessCard detail={data} onChanged={reload} />
        <Card testId="admin-temp-member-card"><SectionTitle hint={t("b8.temp.hint")}>{t("b8.temp.title")}</SectionTitle><TempMemberButton endpoint={`/admin/garages/${g.id}/temp-password-member`} defaultRole="garage_manager" onDone={reload} testId="admin-temp-member-button" /></Card>
        <ManualBillingCard detail={data} onChanged={reload} />
        <TestFlagCard key={`${g.is_test}-${g.test_note}`} detail={data} onChanged={reload} />
        <Card><SuspensionPolicy /></Card>
        <ClearDemoCard detail={data} onChanged={reload} />
        <DangerZoneCard detail={data} />
      </div>
    </div>
  );
}
